import { ValueOf } from 'ts-essentials';
import { ListingResponse } from '~/modules/services/listing_service';
import { FeatureFlagsRecord } from '~/modules/stores/feature_flags_store';
import { WordPressState } from '~/modules/stores/wordpress_store';
import { CompetencyFiltersResponse } from '~/modules/types/competencyFilters';
import { deepFreeze } from '~/modules/utilities/object_utils';
import { ServerListingStoreState } from '~/modules/utilities/server_listing_store_shared';

const _API_VERSIONS = ['1', '2'] as const;
const _SCHEMA_VERSIONS = ['v1'] as const;

export type ApiVersion = (typeof _API_VERSIONS)[number];
export type SchemaVersion = (typeof _SCHEMA_VERSIONS)[number];

export const API_VERSIONS = _API_VERSIONS as unknown as string[];
export const SCHEMA_VERSIONS = _SCHEMA_VERSIONS as unknown as SchemaVersion[];

export const SCHEMA_VERSION: SchemaVersion = 'v1';
export const NEXT_SCHEMA_VERSION: SchemaVersion = 'v1';
export const SCHEMA_VERSION_QUERY_PARAM = 'schemaVersion'; // for API v1 backwards compatibility
export const SCHEMA_VERSION_HEADER = 'SchemaVersion';

export interface MoreFromThisProvider {
    id: string;
    listingUrl: string;
    averageRating: string;
    reviewsAmount: string;
    publisher: string;
    listingCategories: [];
    imageURL: string;
    title: string;
    description: string;
}

export interface CategoryData {
    id: number;
    name: string;
    url: string;
    order: number;
}

export interface CachedListing {
    id: string;
    external_id: string;
    type: string;
    title: string;
    description: string;
    listingCategories: { name: string; filter: string }[];
    logos: { logoType: string; mediaId: string }[];
    publisher: string;
    averageRating: number;
    reviewsAmount: number;
    chatEnabled: boolean;
    listing?: ListingResponse;
    sponsoredCampaigns: AnyObject[];
}

export type SiteDomain = 'appexchange' | 'agentexchange' | 'appexchange-jp';

export interface SiteConfig {
    domain: SiteDomain;
}

export interface InjectedStores {
    FEATURE_FLAGS: FeatureFlagsRecord;
    SITE_CONFIG: SiteConfig;
    ERROR?: { code: string; suppressLandingLog?: boolean };
    LISTING?: ServerListingStoreState;
    WORDPRESS?: WordPressState;
    COMPETENCY_FILTERS?: CompetencyFiltersResponse;
    HOME_CONTENT?: {
        feed: {
            category: Array<CategoryData>;
            sponsored: Array<CachedListing>;
            top_trending: Array<CachedListing>;
        };
        sections: Array<AnyObject>;
    };
}

//#region Try
export const TRY_TYPE_QUERY_PARAM = 'type';

export const TryOptionTypes = deepFreeze({
    None: 'None',
    Sandbox: 'Sandbox',
    TestDrive: 'TestDrive',
    Trialforce: 'Trialforce',
} as const);

export type TryOptionType = ValueOf<typeof TryOptionTypes>;
export type ValidTryOptionType = ValueOf<Omit<typeof TryOptionTypes, 'None'>>;
//#endregion Try

//#region Org profile
// Single source of truth for the product keys — the OrgProducts type and isOrgProfile both derive from it.
export const ORG_PRODUCT_KEYS = [
    'slack',
    'analytics',
    'commerce',
    'integration',
    'marketing',
    'platform',
    'sales',
    'service',
] as const;

// Each toggle is always a boolean (an FLS-denied field is `false`, never tri-state).
export type OrgProducts = Record<(typeof ORG_PRODUCT_KEYS)[number], boolean>;

// Optional scalar fields, `string | null` (null = blank in Org62 or FLS-denied). Mirrors publishing-service's
// OrgTenantProfile — note there is no `sector` field. `orgId` is deliberately NOT here: it must be a non-empty
// string. A null/blank orgId is unusable — buildPersonalizationRequest coerces it to '', collapsing every
// such profile into one personalization dedupe bucket, so we reject it at the boundary instead.
const ORG_PROFILE_SCALAR_KEYS = ['industry', 'region'] as const;

// Tenant metadata for a selected org, from OAF's GET /experience/orgs/:orgId/profile.
export interface OrgProfile {
    orgId: string;
    industry: string | null;
    region: string | null;
    products: OrgProducts;
}

function isStringOrNull(value: unknown): boolean {
    return value === null || typeof value === 'string';
}

// Runtime guard for the fetched body and the persisted read, so a wrong-shape blob is never trusted.
// Lives here beside the type so the service and store share it without a circular import.
export function isOrgProfile(value: unknown): value is OrgProfile {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const candidate = value as Record<string, unknown>;
    // orgId must be a non-empty string — it keys personalization; a null/blank value is unusable.
    if (typeof candidate.orgId !== 'string' || candidate.orgId.length === 0) {
        return false;
    }
    if (!ORG_PROFILE_SCALAR_KEYS.every((key) => isStringOrNull(candidate[key]))) {
        return false;
    }
    const products = candidate.products;
    if (typeof products !== 'object' || products === null) {
        return false;
    }
    const productMap = products as Record<string, unknown>;
    return ORG_PRODUCT_KEYS.every((key) => typeof productMap[key] === 'boolean');
}

// Rebuilds an OrgProfile from only the known keys, so a validated-but-extra-field body (e.g. an
// extra key nested under `products`) can't carry unexpected fields through a privileged proxy.
// Pairs with isOrgProfile: validate first, then project the result. The products literal is checked
// against OrgProducts, so it stays in sync with ORG_PRODUCT_KEYS without a cast.
export function projectOrgProfile(profile: OrgProfile): OrgProfile {
    const p = profile.products;
    return {
        orgId: profile.orgId,
        industry: profile.industry,
        region: profile.region,
        products: {
            slack: p.slack,
            analytics: p.analytics,
            commerce: p.commerce,
            integration: p.integration,
            marketing: p.marketing,
            platform: p.platform,
            sales: p.sales,
            service: p.service,
        },
    };
}
//#endregion Org profile
